/**
 * Shared shape configuration form.
 * Callsign, stroke color, stroke weight, fill color + opacity.
 * Reused by drawing, route, and geofence tabs.
 */

import { useDrawingStore } from '@/stores/drawing-store';
import { argbIntToHex, hexToArgbInt, argbAlpha } from '@vtak/shared';

/** Shared shape styling configuration form */
export function ShapeConfig(): React.JSX.Element {
  const config = useDrawingStore((s) => s.shapeConfig);
  const updateConfig = useDrawingStore((s) => s.updateConfig);

  const strokeHex = argbIntToHex(config.strokeColor);
  const fillHex = argbIntToHex(config.fillColor);
  const fillOpacity = argbAlpha(config.fillColor);

  return (
    <div className="space-y-3">
      <div>
        <label className="text-xs text-muted-foreground">Callsign</label>
        <input
          type="text"
          value={config.callsign}
          onChange={(e) => updateConfig({ callsign: e.target.value })}
          placeholder="Shape name..."
          className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground placeholder:text-muted-foreground outline-none focus:ring-1 focus:ring-echo-yellow"
        />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Stroke Color</label>
          <input
            type="color"
            value={strokeHex}
            onChange={(e) => updateConfig({ strokeColor: hexToArgbInt(e.target.value, 1) })}
            className="mt-1 h-8 w-full cursor-pointer rounded bg-transparent"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Stroke Weight</label>
          <input
            type="number"
            min={1}
            max={20}
            step={1}
            value={config.strokeWeight}
            onChange={(e) => updateConfig({ strokeWeight: Number(e.target.value) })}
            className="mt-1 w-full rounded bg-white/[0.06] px-3 py-1.5 text-sm text-foreground outline-none"
          />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-xs text-muted-foreground">Fill Color</label>
          <input
            type="color"
            value={fillHex}
            onChange={(e) => updateConfig({ fillColor: hexToArgbInt(e.target.value, fillOpacity) })}
            className="mt-1 h-8 w-full cursor-pointer rounded bg-transparent"
          />
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Fill Opacity</label>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={fillOpacity}
            onChange={(e) => updateConfig({ fillColor: hexToArgbInt(fillHex, Number(e.target.value)) })}
            className="mt-3 w-full accent-echo-yellow"
          />
        </div>
      </div>
    </div>
  );
}
